import React, { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from './lp2/Navigation';
import api, { errorMessage } from './api';
import { Button } from './lp2/ui/button.tsx';
import { User, Mail, Phone, Shield, LogOut, CheckCircle, XCircle, CreditCard, Users, Clock, MessageCircle, Heart } from 'lucide-react';

const Profile = () => {
    const [userData, setUserData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    const navigate = useNavigate();

    // Modus wie serverseitig: allein aus checkInName. Kein eigenes Flag.
    const fuerAngehoerigen = Boolean(userData?.checkInName);
    const betreutePerson = userData?.checkInName;

    const [kanalLaedt, setKanalLaedt] = useState(false);
    const [kanalFehler, setKanalFehler] = useState(null);
    const [kanalGespeichert, setKanalGespeichert] = useState(false);

    const kanalAendern = async (feld, wert) => {
        setKanalLaedt(true); setKanalFehler(null); setKanalGespeichert(false);
        try {
            const r = await api.patch('/profile', { [feld]: wert });
            setUserData(r.data);
            setKanalGespeichert(true);
        } catch (err) {
            setKanalFehler(errorMessage(err, 'Änderung fehlgeschlagen.'));
        } finally {
            setKanalLaedt(false);
        }
    };

    const handleSignOut = () => {
        localStorage.removeItem('token'); 
        navigate('/login'); 
    };

    useEffect(() => {
        const message = localStorage.getItem('paymentSuccess');
        if (message) {
            setSuccessMessage(message);
            localStorage.removeItem('paymentSuccess');
        }

        const fetchUserData = async () => {
            try {
                // Vorher fest auf die deployte API verdrahtet – die lokale
                // Entwicklung sprach damit die Produktivdaten an.
                const response = await api.get('/profile');
                setUserData(response.data);
                setIsLoading(false);
            } catch (err) {
                if (err?.response?.status === 401) {
                    localStorage.removeItem('token');
                    navigate('/login');
                    return;
                }
                setError(errorMessage(err, 'Profil konnte nicht geladen werden.'));
                setIsLoading(false);
            }
        };

        fetchUserData();
    }, [navigate]);

    if (isLoading) return (
        <Fragment>
            <Navigation />
            <section className="bg-gradient-to-b from-blue-50 to-white min-h-screen py-20 px-4">
                <div className="max-w-4xl mx-auto">
                    <div className="flex justify-center items-center min-h-[400px]">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                            <p className="text-gray-600">Lade Profil...</p>
                        </div>
                    </div>
                </div>
            </section>
        </Fragment>
    );
    
    if (error) return (
        <Fragment>
            <Navigation />
            <section className="bg-gradient-to-b from-blue-50 to-white min-h-screen py-20 px-4">
                <div className="max-w-4xl mx-auto">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                        <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-red-800 mb-2">Fehler beim Laden</h2>
                        <p className="text-red-600">{error}</p>
                    </div>
                </div>
            </section>
        </Fragment>
    );

    return (
        <Fragment>
            <Navigation />
            <section className="bg-gradient-to-b from-blue-50 to-white min-h-screen py-20 px-4">
                <div className="max-w-4xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-12">
                        <div className="flex justify-center mb-6">
                            <div className="bg-blue-100 p-4 rounded-full">
                                <User className="w-12 h-12 text-blue-600" />
                            </div>
                        </div>
                        
                        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Mein Profil
                        </h1>
                        
                        <p className="text-lg text-gray-600">
                            Verwalten Sie Ihre Kontoinformationen und Einstellungen
                        </p>
                    </div>

                    {/* Success Message */}
                    {successMessage && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <CheckCircle className="h-5 w-5 text-green-400" />
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-green-800">{successMessage}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* What happens next section - shown for new users */}
                    {successMessage && (
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-8 mb-8">
                            <div className="flex items-center mb-6">
                                <div className="bg-blue-100 p-3 rounded-full mr-4">
                                    <Clock className="w-6 h-6 text-blue-600" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900">Was passiert als nächstes?</h2>
                                    <p className="text-gray-600">Ihr Service ist erfolgreich eingerichtet</p>
                                </div>
                            </div>
                            
                            <div className="space-y-4">
                                <div className="flex items-start">
                                    <div className="bg-blue-100 p-2 rounded-full mr-4 mt-1">
                                        <MessageCircle className="w-4 h-4 text-blue-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-gray-900 mb-1">Erste Nachricht</h3>
                                        <p className="text-gray-700">
                                            Spätestens morgen erhalten Sie Ihre erste Tagesmeldung an Ihre registrierte Telefonnummer.
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="flex items-start">
                                    <div className="bg-green-100 p-2 rounded-full mr-4 mt-1">
                                        <CheckCircle className="w-4 h-4 text-green-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-gray-900 mb-1">Fenster schließen</h3>
                                        <p className="text-gray-700">
                                            Sie können dieses Fenster jetzt schließen. Der Service läuft automatisch weiter.
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="flex items-start">
                                    <div className="bg-orange-100 p-2 rounded-full mr-4 mt-1">
                                        <Mail className="w-4 h-4 text-orange-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-gray-900 mb-1">Anpassungen gewünscht?</h3>
                                        <p className="text-gray-700">
                                            Falls Sie Änderungen an der Uhrzeit oder anderen Einstellungen wünschen, 
                                            senden Sie uns eine E-Mail an{' '}
                                            <a 
                                                href="mailto:office@esgehtmirgut.ch" 
                                                className="text-blue-600 hover:text-blue-800 underline font-medium"
                                            >
                                                office@esgehtmirgut.ch
                                            </a>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {userData ? (
                        <div className="space-y-8">
                            {/* Personal Information Card */}
                            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                                <div className="flex items-center mb-6">
                                    <User className="w-6 h-6 text-blue-600 mr-3" />
                                    <h2 className="text-xl font-semibold text-gray-900">Persönliche Informationen</h2>
                                </div>
                                
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {fuerAngehoerigen ? 'Ihr Name' : 'Name'}
                                            </label>
                                            <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                                                <User className="w-5 h-5 text-gray-400 mr-3" />
                                                <span className="text-gray-900 font-medium">{userData.name}</span>
                                            </div>
                                        </div>
                                        
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">E-Mail</label>
                                            <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                                                <Mail className="w-5 h-5 text-gray-400 mr-3" />
                                                <span className="text-gray-900 font-medium">{userData.email}</span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="space-y-4">
                                        {fuerAngehoerigen && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Betreute Person</label>
                                                <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                                                    <Heart className="w-5 h-5 text-gray-400 mr-3" />
                                                    <span className="text-gray-900 font-medium">{betreutePerson}</span>
                                                </div>
                                            </div>
                                        )}

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {fuerAngehoerigen
                                                    ? `Handynummer von ${betreutePerson}`
                                                    : 'Telefonnummer'}
                                            </label>
                                            <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                                                <Phone className="w-5 h-5 text-gray-400 mr-3" />
                                                <span className="text-gray-900 font-medium">{userData.phoneNumber}</span>
                                            </div>
                                            {fuerAngehoerigen && (
                                                <p className="mt-1 text-sm text-gray-500">
                                                    Hierhin geht die tägliche Nachricht.
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {fuerAngehoerigen ? 'Ihre Handynummer' : 'Kontaktperson'}
                                            </label>
                                            <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                                                <Users className="w-5 h-5 text-gray-400 mr-3" />
                                                <span className="text-gray-900 font-medium">{userData.emergencyContactPhoneNumber}</span>
                                            </div>
                                            {fuerAngehoerigen && (
                                                <p className="mt-1 text-sm text-gray-500">
                                                    Hierhin melden wir uns, wenn keine Antwort kommt.
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Kanal-Einstellungen. Die Auswahl ist aus der
                                Registrierung entfernt worden – sie muss hier
                                erreichbar sein, sonst stimmt die Zusage nicht. */}
                            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                                <h2 className="text-xl font-semibold text-gray-900 mb-1">
                                    Wie sollen wir schreiben?
                                </h2>
                                <p className="text-gray-600 text-sm mb-6">
                                    SMS erreicht jedes Handy. WhatsApp nur, wenn es auf dem Gerät eingerichtet ist.
                                </p>

                                {kanalFehler && (
                                    <div role="alert" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                                        {kanalFehler}
                                    </div>
                                )}
                                {kanalGespeichert && (
                                    <p className="mb-4 text-sm text-green-700">Gespeichert.</p>
                                )}

                                <div className="grid sm:grid-cols-2 gap-6">
                                    {[
                                        { key: 'preferredChannel',
                                          label: fuerAngehoerigen
                                            ? `Tagesmeldung an ${betreutePerson}`
                                            : 'Ihre Tagesmeldung' },
                                        { key: 'preferredChannelEmergencyContact',
                                          label: fuerAngehoerigen
                                            ? 'Benachrichtigung an Sie'
                                            : 'Benachrichtigung der Kontaktperson' },
                                    ].map(({ key, label }) => (
                                        <div key={key}>
                                            <label htmlFor={key} className="block text-sm font-medium text-gray-700 mb-2">
                                                {label}
                                            </label>
                                            <select
                                                id={key}
                                                value={userData[key] || 'sms'}
                                                disabled={kanalLaedt}
                                                onChange={(e) => kanalAendern(key, e.target.value)}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg disabled:opacity-50"
                                            >
                                                <option value="sms">SMS</option>
                                                <option value="whatsapp">WhatsApp</option>
                                            </select>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Subscription Status Card */}
                            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                                <div className="flex items-center mb-6">
                                    <CreditCard className="w-6 h-6 text-blue-600 mr-3" />
                                    <h2 className="text-xl font-semibold text-gray-900">Abonnement-Status</h2>
                                </div>
                                
                                <div className="flex items-center justify-between p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                                    <div className="flex items-center">
                                        {userData.paymentStatus === "paid" ? (
                                            <CheckCircle className="w-8 h-8 text-green-500 mr-4" />
                                        ) : (
                                            <XCircle className="w-8 h-8 text-red-500 mr-4" />
                                        )}
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900">
                                                {userData.paymentStatus === "paid" ? "Aktiviert" : "Nicht aktiviert"}
                                            </h3>
                                            <p className="text-gray-600">
                                                {userData.paymentStatus === "paid" 
                                                    ? "Ihr Abonnement ist aktiv und Sie erhalten tägliche Nachrichten." 
                                                    : "Bitte kontaktieren Sie uns, um Ihr Abonnement zu aktivieren."}
                                            </p>
                                        </div>
                                    </div>
                                    
                                    {userData.paymentStatus !== "paid" && (
                                        <Button 
                                            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
                                            onClick={() => navigate('/payment')}
                                        >
                                            Aktivieren
                                        </Button>
                                    )}
                                </div>
                            </div>

                            {/* Security Card */}
                            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                                <div className="flex items-center mb-6">
                                    <Shield className="w-6 h-6 text-blue-600 mr-3" />
                                    <h2 className="text-xl font-semibold text-gray-900">Sicherheit</h2>
                                </div>
                                
                                <div className="space-y-4">
                                    <p className="text-gray-600 mb-6">
                                        Ihre Daten werden sicher verschlüsselt und nur für den Service verwendet.
                                    </p>
                                    
                                    <Button 
                                        onClick={handleSignOut}
                                        className="w-full bg-red-600 hover:bg-red-700 text-white py-3 px-6 rounded-lg font-medium transition-colors"
                                    >
                                        <LogOut className="w-5 h-5 mr-2" />
                                        Abmelden
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 text-center">
                            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                            <h2 className="text-xl font-semibold text-gray-900 mb-2">Profil nicht geladen</h2>
                            <p className="text-gray-600 mb-6">
                                Vermutlich sind Sie nicht eingeloggt. Bitte melden Sie sich erneut an.
                            </p>
                            <Button 
                                onClick={() => navigate('/login')}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
                            >
                                Zum Login
                            </Button>
                        </div>
                    )}
                </div>
            </section>
        </Fragment>
    );
};

export default Profile;
